import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// AnalyzerHeader is the consistent top-of-panel block for every
// analyser sub-tab. Renders title + icon + advisory chip + Run button
// + an expandable "What does this do?" section sourced from the
// algorithm catalogue.
//
// `extraHelpContent` is an optional ReactNode slot rendered at the
// bottom of the expanded "How it works" panel, after the standard
// description / algorithm / measures-and-acts-on block. It exists so
// richer per-panel explainers (e.g. the chained pipeline diagram on
// the Optimize/Meta tab) can live inside the same help dropdown
// instead of cluttering the analyser panel body.
const AnalyzerHeader = ({
  catalog,
  loading,
  onRun,
  hasResult,
  rightContent = null,
  extraHelpContent = null,
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const Icon = catalog.icon;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
          {Icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: `${catalog.color}.main`,
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon />
            </Box>
          )}
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {catalog.title}
              </Typography>
              {catalog.advisory && (
                <Tooltip title="Advisory analyser — produces a single-snapshot reading; no automatic actions.">
                  <Chip label="Advisory" size="small" variant="outlined" />
                </Tooltip>
              )}
              <Tooltip title={catalog.tooltip}>
                <IconButton size="small" onClick={() => setHelpOpen((v) => !v)}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {catalog.short}
            </Typography>
          </Box>
        </Stack>

        {rightContent && (
          <Box sx={{ flexGrow: 0 }}>
            {rightContent}
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={helpOpen ? 'Hide details' : 'Show details'}>
            <Button
              size="small"
              variant="text"
              endIcon={helpOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setHelpOpen((v) => !v)}
            >
              How it works
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            color={catalog.color}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={onRun}
            disabled={loading}
          >
            {hasResult ? 'Re-run' : 'Run'}
          </Button>
        </Stack>
      </Stack>

      <Collapse in={helpOpen}>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            {catalog.description}
          </Typography>
          {catalog.algorithm && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Algorithm
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {catalog.algorithm}
              </Typography>
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Measured by
              </Typography>
              <Typography variant="body2">{catalog.measures}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Acts on
              </Typography>
              <Typography variant="body2">{catalog.actsOn}</Typography>
            </Box>
          </Stack>

          {/* Optional richer explainer (e.g. the chained pipeline
              diagram on the Optimize/Meta tab). Lives inside the same
              "How it works" dropdown so panels stay tidy and all the
              "what does this do?" content is in one discoverable
              place. */}
          {extraHelpContent && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px dashed',
                borderColor: 'divider',
              }}
            >
              {extraHelpContent}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AnalyzerHeader;
